import * as React from 'react'
import * as Cookies from 'js-cookie'

import { HeaderZIndexLevel } from '@thg-commerce/enterprise-components/Header/types'
import { useLogger } from '@thg-commerce/enterprise-core'
import { pushToDataLayer } from '@thg-commerce/enterprise-metrics/src/data_layer'
import {
  mq,
  spacing,
  styled,
  Text,
  zIndex,
} from '@thg-commerce/enterprise-theme'
import { Button } from '@thg-commerce/gravity-elements'
import { KeyboardKeys } from '@thg-commerce/gravity-theme'

import { useCustomer, useLoginCheck } from '../CustomerContext'
import { i18n } from '../i18n/'
import { useWishlistNotificationContext } from '../WishlistNotificationProvider'

const StyledNotificationWrapper = styled.div`
  position: absolute;
  background: ${(props) => props.theme.colors.palette.greys.white};
  width: 272px;
  border: 1px solid ${(props) => props.theme.colors.palette.greys.grey};
  top: 45px;
  right: -14px;
  padding: 16px;
  ${zIndex(HeaderZIndexLevel.Dropdown)};

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    width: 300px;
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'lg')} {
    right: -49px;
  }

  &:before {
    position: absolute;
    display: block;
    top: -8px;
    width: 16px;
    height: 16px;
    left: auto;
    right: 29px;
    background: ${(props) => props.theme.colors.palette.greys.white};
    content: '';
    transform: rotate(45deg);
    border-width: 1px;
    border-style: solid;
    border-color: ${(props) => props.theme.colors.palette.greys.grey};
    border-right-color: transparent;
    border-bottom-color: transparent;

    ${(props) => mq(props.theme.breakpointUtils.map, 'lg')} {
      right: 64px;
    }
  }
`

const WishlistNotificationWelcome = styled.div`
  ${Text('bodyText', 'alternate')};
`

const WishlistNotificationSubtext = styled.div`
  ${Text('bodyText', 'default')};
  padding-top: ${spacing(2)};
`

const GotItButton = styled(Button)`
  flex: 1;
  margin-top: ${spacing(3)};
  width: 100%;
`

const WishListLink = styled(Button)`
  flex: 1;
  margin-top: ${spacing(2)};
  width: 100%;
`

const WISHLIST_SHOWN_COOKIE_NAME = 'wishlistsTooltipModalCookie'

export const WishlistNotification: React.FunctionComponent = () => {
  const notificationRef = React.useRef<HTMLDivElement>(null)
  const customer = useCustomer()
  const isLoggedIn = useLoginCheck()
  const logger = useLogger()

  const { showWishlistNotification } = useWishlistNotificationContext()

  const [visible, setVisible] = React.useState(false)
  const confirmButtonRef = React.useRef<HTMLButtonElement>(null)

  const wishlistCookie = Cookies.get(WISHLIST_SHOWN_COOKIE_NAME)

  React.useEffect(() => {
    if (!visible) {
      return
    }

    pushToDataLayer({
      type: 'elysiumEvent',
      eventData: {
        eventCategory: 'Wishlist Engagement',
        eventAction: 'Wishlists Modal | Shown',
        eventLabel: 'Wishlists Modal Message',
      },
    })
  }, [visible])

  React.useEffect(() => {
    if (!visible) {
      return
    }

    Cookies.set(WISHLIST_SHOWN_COOKIE_NAME, WISHLIST_SHOWN_COOKIE_NAME, {
      expires: 7,
    })
  }, [visible])

  React.useEffect(() => {
    visible && confirmButtonRef.current && confirmButtonRef.current.focus()
  }, [visible, confirmButtonRef])

  const i18nText = {
    welcome: i18n('wishlists.tooltip.modal.welcome.text'),
    message: i18n('wishlists.tooltip.modal.text.message'),
    gotit: i18n('wishlists.tooltip.modal.acknowledge.button'),
    explore: i18n('wishlists.tooltip.modal.explore.button'),
  }

  const closeWithKeyPress = (event: React.KeyboardEvent) => {
    event.stopPropagation()
    if (event.key === KeyboardKeys.Escape) {
      setVisible(false)

      const headerAccountButton = document.querySelector(
        '[data-testid="account-trigger-wrapper"] [data-testid="header-trigger-closed"]',
      ) as HTMLElement
      headerAccountButton?.focus()
    }
  }

  if (!showWishlistNotification) {
    logger.warn(
      'Failed to initialise WishlistNotification because no WishListNotificationProvider could be found',
    )
    return null
  }

  showWishlistNotification.current = () => {
    if (
      wishlistCookie ||
      !isLoggedIn ||
      customer?.wishlist?.itemCount ||
      0 > 0
    ) {
      return
    }

    setVisible(true)
  }

  if (!visible) {
    return null
  }

  return (
    <StyledNotificationWrapper
      data-testid="wishlist-notification"
      ref={notificationRef}
      onKeyDown={closeWithKeyPress}
      onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
        if (!notificationRef?.current?.contains(event.relatedTarget as Node)) {
          setVisible(false)
        }
      }}
    >
      <WishlistNotificationWelcome data-testid="wishlist-notification-welcome">
        {i18nText.welcome}, {customer?.firstName}
      </WishlistNotificationWelcome>
      <WishlistNotificationSubtext>
        {i18nText.message}
      </WishlistNotificationSubtext>
      <GotItButton
        ref={confirmButtonRef}
        emphasis="high"
        onClick={() => {
          setVisible(false)

          pushToDataLayer({
            type: 'elysiumEvent',
            eventData: {
              eventCategory: 'Wishlist Engagement',
              eventAction: 'Wishlists Modal | Close',
            },
          })
        }}
        data-testid="wishlist-notification-gotit"
      >
        {i18nText.gotit}
      </GotItButton>
      <WishListLink
        renderedAs="a"
        emphasis="medium"
        href="/wishlists.account"
        data-testid="wishlist-notification-explore"
        onClick={() =>
          pushToDataLayer({
            type: 'elysiumEvent',
            eventData: {
              eventCategory: 'Wishlist Engagement',
              eventAction: 'Wishlists Modal | Clicked explore button',
            },
          })
        }
      >
        {i18nText.explore}
      </WishListLink>
    </StyledNotificationWrapper>
  )
}

export default WishlistNotification
